
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Section from './Section'
import ReadOnly from './ReadOnly'

const headings = [
  'Date & Time',
  'Location',
  'Litres Purchased',
  'Over Tank Capacity',
  'Product Purchased',
  'Odometer Reading',
]

const MultiFillTable = ({
  rows,
  formatDateTime,
  formatLitresWithValue,
  getOverTankCapacityLabel,
  odometerFormatter,
  maxHeight,
}) => (
  <Box sx={{ mt: 2 }}>
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 1020, maxHeight, overflowY: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, minmax(140px, 1fr))',
            gap: 1,
            borderBottom: '1px solid #E0E0E0',
            pb: 1,
          }}>
          {headings.map((heading) => (
            <Typography
              key={heading}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                color: '#617282',
              }}>
              {heading}
            </Typography>
          ))}
        </Box>

        {rows.map((row, index) => (
          <Box
            key={`${row.transactionDate || 'row'}-${row.transactionTime || index}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, minmax(140px, 1fr))',
              gap: 1,
              borderBottom:
                index === rows.length - 1 ? 'none' : '1px solid #F0F3F5',
              py: 1,
            }}>
            <Typography sx={{ fontSize: 12 }}>
              {formatDateTime(row.transactionDate, row.transactionTime)}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              {row.locationName || '-'}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              {formatLitresWithValue(row.litres, row.customerValue)}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              {getOverTankCapacityLabel(
                row.litres,
                row.tankSize,
                row.overTankCapacity
              )}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              {row.productPurchased || '-'}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              {(() => {
                const value =
                  row.odometerReading ?? row.odometer ?? row.odometerValue
                const num = Number(value)
                return Number.isFinite(num)
                  ? odometerFormatter.format(num)
                  : value || '-'
              })()}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
)

MultiFillTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  formatDateTime: PropTypes.func.isRequired,
  formatLitresWithValue: PropTypes.func.isRequired,
  getOverTankCapacityLabel: PropTypes.func.isRequired,
  odometerFormatter: PropTypes.instanceOf(Intl.NumberFormat).isRequired,
  maxHeight: PropTypes.number,
}

MultiFillTable.defaultProps = {
  maxHeight: 240,
}

const ExceptionInformationSection = ({
  data,
  exceptionTitle,
  issueSummaryText,
  litresPurchasedValue,
  overTankCapacityValue,
  productPurchasedValue,
  odometerValue,
  multiFillRows,
  formatDate,
  formatDateTime,
  formatLitresWithValue,
  getOverTankCapacityLabel,
  odometerFormatter,
  isMultiFill,
}) => {
  return (
    <>
      <Grid item xs={12} md={isMultiFill ? 6 : 8}>
        <Box sx={{ mr: 3 }}>
          <Section title="Exception Information">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  md: isMultiFill ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                },
                gap: 1,
              }}>
              {!isMultiFill && (
                <ReadOnly label="Date & Time" value={formatDate(data?.transactionDate)} />
              )}

              <ReadOnly label="Exception Issue" value={data?.exception} />
              <ReadOnly label="Card Number" value={data?.cardnumber} />

              {!isMultiFill && (
                <>
                  <ReadOnly label="Location of Exception" value={data?.locationName} />
                  <ReadOnly label="Litres Purchased" value={litresPurchasedValue} />
                  <ReadOnly label="Over Tank Capacity" value={overTankCapacityValue} />
                  <ReadOnly label="Product Purchased" value={productPurchasedValue} />
                  <ReadOnly label="Odometer Reading" value={odometerValue} />
                </>
              )}
            </Box>

            {/* {!isMultiFill && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mt: 2,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#F04438",
                      mb: 1,
                    }}
                  >
                    {exceptionTitle}
                  </Typography>

                  <Typography sx={{ fontSize: 12, lineHeight: "18px" }}>
                    {issueSummaryText}
                  </Typography>
                </Box>

                <WarningAmberIcon sx={{ color: "#F04438", mt: "2px" }} />
              </Box>
            )} */}
            {/*
            {showMultiFillTable && (
              <MultiFillTable
                rows={multiFillRows}
                formatDateTime={formatDateTime}
                formatLitresWithValue={formatLitresWithValue}
                getOverTankCapacityLabel={getOverTankCapacityLabel}
                odometerFormatter={odometerFormatter}
                maxHeight={240}
              />
            )} */}
          </Section>
        </Box>
      </Grid>

      {isMultiFill ? (
        <>
          <Grid item xs={12} md={6}>
            <Section title="Other">
              <Grid container>
                <Grid item xs={12} md={4}>
                  <ReadOnly
                    label="Location"
                    value={data?.locationName?.split(',').pop()
                      ?.trim() || ''}/>
                </Grid>

                <Grid item xs={12} md={4}>
                  <ReadOnly label="Fleet Controller" value={data?.fleetController} />
                </Grid>

                <Grid item xs={12} md={4}>
                  <ReadOnly label="Customer Name" value={data?.customerName} />
                </Grid>
              </Grid>
            </Section>
          </Grid>
        </>
      ) : (
        <Grid item xs={12} md={4}>
          <Section title="Other">
            <Grid container>
              <Grid item xs={12}>
                <ReadOnly label="Customer Cost Center" value={data?.costCentre} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ReadOnly
                  label="Location"
                  value={data?.locationName?.split(',').pop()
                    ?.trim() || ''}/>
              </Grid>

              <Grid item xs={12} md={6}>
                <ReadOnly
                  label="Fleet Controller"
                  value={data?.fleetController}/>
              </Grid>
            </Grid>
          </Section>
        </Grid>
      )}
    </>
  )
}

ExceptionInformationSection.propTypes = {
  data: PropTypes.object.isRequired,
  exceptionTitle: PropTypes.string.isRequired,
  issueSummaryText: PropTypes.string.isRequired,
  litresPurchasedValue: PropTypes.string.isRequired,
  overTankCapacityValue: PropTypes.string.isRequired,
  productPurchasedValue: PropTypes.string.isRequired,
  odometerValue: PropTypes.string.isRequired,
  multiFillRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  formatDate: PropTypes.func.isRequired,
  formatDateTime: PropTypes.func.isRequired,
  formatLitresWithValue: PropTypes.func.isRequired,
  getOverTankCapacityLabel: PropTypes.func.isRequired,
  odometerFormatter: PropTypes.instanceOf(Intl.NumberFormat).isRequired,
  isMultiFill: PropTypes.bool.isRequired,
}

export { MultiFillTable }
export default ExceptionInformationSection
